import { useEffect, useState } from 'react';
// Advertising is gone (see `AdSlot`); the import stays as a marker of where it was.
// import { AdSlot } from '../components/AdSlot.js';
import { InstallCard } from '../components/InstallCard.js';
import { SiteFooter, type FooterView } from '../components/SiteFooter.js';
import { SlothHero, SlothMark } from '../components/Sloth.js';
import { workshopApi } from '../api/workshop.js';
import { COMMUNITY_CATALOG } from '../lib/features.js';
import { useT } from '../i18n/index.js';
import { useAccount } from '../session/useAccount.js';
import { UserIcon } from '../components/icons.js';

interface HomeScreenProps {
  onCreate: () => void;
  onJoin: () => void;
  onWorkshop: () => void;
  onWatchlist: () => void;
  onAccount: () => void;
  onNavigate: (view: FooterView) => void;
  onDeviceSettings: () => void;
}

export function HomeScreen({
  onCreate,
  onJoin,
  onWorkshop,
  onWatchlist,
  onAccount,
  onNavigate,
  onDeviceSettings,
}: HomeScreenProps) {
  const t = useT();
  const { account } = useAccount();
  const [openPoints, setOpenPoints] = useState<number | null>(null);

  // The size of the backlog is the whole pitch for the workshop, so it is
  // worth one request on the landing screen. A failure leaves it unset rather
  // than showing a zero, which would read as "nothing to do here".
  useEffect(() => {
    // Nothing to count while the community catalog is switched off.
    if (!COMMUNITY_CATALOG) return;
    let cancelled = false;
    void workshopApi
      .summary()
      .then((summary) => {
        if (!cancelled) setOpenPoints(summary.openTotal);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="screen">
      <div className="row row--between">
        <div className="brand">
          <span className="brand__mark">
            <SlothMark size={30} />
          </span>
          <span>{t('app.name')}</span>
        </div>
        {/* The account button is the only place the sign-in state shows on the
            landing screen, and that is deliberate: an account changes nothing
            about starting or joining a round, so putting it anywhere more
            prominent would suggest otherwise. */}
        <button type="button" className="btn btn--small home__account" onClick={onAccount}>
          <UserIcon />
          <span className="home__account-label">
            {account ? account.username : t('home.signIn')}
          </span>
        </button>
      </div>

      <div className="stack home__intro">
        <SlothHero className="home__hero" />
        <h1>{t('app.tagline')}</h1>
      </div>

      <div className="stack">
        <button type="button" className="btn btn--primary btn--lg btn--block" onClick={onCreate}>
          {t('home.create')}
        </button>
        <p className="muted small center">{t('home.createHint')}</p>
      </div>

      <div className="stack">
        <button type="button" className="btn btn--lg btn--block" onClick={onJoin}>
          {t('home.join')}
        </button>
        <p className="muted small center">{t('home.joinHint')}</p>
      </div>

      {/* Two small tools side by side rather than two more full-width
          buttons: stacked, they looked like a third and fourth way to start
          the evening and pushed the workshop below the fold. */}
      <div className="home__tools">
        <button type="button" className="btn btn--ghost btn--small" onClick={onWatchlist}>
          ★ {t('watchlist.open')}
        </button>
        {/* Next to the watchlist rather than in the footer below: the footer
            is the standing pages, each with an address a crawler follows, and
            what is behind this is a pair of switches for this one phone. */}
        <button type="button" className="btn btn--ghost btn--small" onClick={onDeviceSettings}>
          ⚙ {t('home.deviceSettings')}
        </button>
      </div>

      <div className="spacer" />

      {/* The workshop is switched off since the catalog comes from TMDB; the
          panel comes back with `COMMUNITY_CATALOG`. */}
      {COMMUNITY_CATALOG ? (
        <div className="panel stack home__workshop">
          <div className="row row--between">
            <h2>{t('home.workshop')}</h2>
          </div>
          <p className="muted small">
            {account && openPoints !== null
              ? t('home.workshopHint', { count: openPoints.toLocaleString() })
              : t('home.workshopLocked')}
          </p>
          <button
            type="button"
            className={account ? 'btn btn--primary btn--block' : 'btn btn--block'}
            onClick={account ? onWorkshop : onAccount}
          >
            {account ? t('home.workshopOpen') : t('home.workshopSignIn')}
          </button>
        </div>
      ) : null}

      {/* No more advertising, and so no ad-free reward either.
      {adFree && account?.adFreeUntil ? (
        <p className="home__adfree small">
          {t('home.adFreeActive', {
            date: new Date(account.adFreeUntil).toLocaleDateString(),
          })}
        </p>
      ) : (
        <AdSlot />
      )}
      */}

      <InstallCard />

      {/* Not just the imprint any more: about, contact, privacy and the legal
          notice all hang here, on the one screen every visitor passes through.
          A site that hides them behind a menu is a site whose visitors — and
          whose ad network — conclude nobody is home. */}
      <SiteFooter current="home" onNavigate={onNavigate} />
    </div>
  );
}
